import { useEffect, useRef } from "react";
import {
  ArrowRight,
  Bot,
  Globe,
  LayoutDashboard,
  Mic,
  MonitorSmartphone,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { services } from "@/data/siteContent";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import useScrollReveal from "@/hooks/useScrollReveal";
import useHorizontalRibbon from "@/hooks/useHorizontalRibbon";
import { scrollToSection } from "@/hooks/useLenis";
import SectionHeading from "@/components/SectionHeading";
import Img from "@/components/Img";

const SERVICE_ICONS = {
  Globe,
  MonitorSmartphone,
  ShoppingBag,
  Bot,
  LayoutDashboard,
  Mic,
};

function ServiceIcon({ name, className, size = 22 }) {
  const Icon = SERVICE_ICONS[name] || Sparkles;
  return <Icon size={size} strokeWidth={1.4} className={className} aria-hidden />;
}

/**
 * Premium 3D flip service card.
 * Front: editorial image + number / title / icon. Back: blue glass + copy (unchanged motion).
 */
function ServiceCard({ item, reducedMotion }) {
  const rootRef = useRef(null);
  const innerRef = useRef(null);
  const imageRef = useRef(null);
  const backIconRef = useRef(null);
  const backTitleRef = useRef(null);
  const backDescRef = useRef(null);
  const backCtaRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const inner = innerRef.current;
    if (!root || !inner) return undefined;

    const ctx = gsap.context(() => {
      gsap.set(inner, { rotateY: 0, transformPerspective: 1200 });
      gsap.set(root, { y: 0 });
      if (imageRef.current) {
        gsap.set(imageRef.current, { scale: 1, y: 0, filter: "brightness(1)" });
      }

      if (reducedMotion) {
        gsap.set(backIconRef.current, { scale: 1, autoAlpha: 1 });
        gsap.set(backTitleRef.current, { y: 0, autoAlpha: 1 });
        gsap.set(backDescRef.current, { y: 0, autoAlpha: 1 });
        gsap.set(backCtaRef.current, { y: 0, autoAlpha: 1 });

        const tl = gsap.timeline({ paused: true, defaults: { ease: "power2.out" } });
        tlRef.current = tl;
        tl.to(inner, { rotateY: 180, duration: 0.01 }, 0);
        return;
      }

      gsap.set(backIconRef.current, { scale: 0.88, autoAlpha: 0.65 });
      gsap.set(backTitleRef.current, { y: 10, autoAlpha: 0.85 });
      gsap.set(backDescRef.current, { y: 16, autoAlpha: 0 });
      gsap.set(backCtaRef.current, { y: 8, autoAlpha: 0 });

      const tl = gsap.timeline({
        paused: true,
        defaults: { ease: "power3.inOut" },
      });
      tlRef.current = tl;

      // Pre-flip: subtle image cinematic (completes as lift settles; flip timing unchanged)
      if (imageRef.current) {
        tl.to(
          imageRef.current,
          {
            scale: 1.04,
            y: -4,
            filter: "brightness(1.06)",
            duration: 0.35,
            ease: "power2.out",
          },
          0
        );
      }

      // Existing flip sequence — timings preserved
      tl.to(root, { y: -6, duration: 0.35, ease: "power2.out" }, 0)
        .to(
          inner,
          {
            rotateY: 180,
            duration: 0.62,
            ease: "power3.inOut",
          },
          0.08
        )
        .to(
          backIconRef.current,
          { scale: 1, autoAlpha: 1, duration: 0.4, ease: "power2.out" },
          0.42
        )
        .to(
          backTitleRef.current,
          { y: 0, autoAlpha: 1, duration: 0.4, ease: "power2.out" },
          0.45
        )
        .to(
          backDescRef.current,
          { y: 0, autoAlpha: 1, duration: 0.45, ease: "power2.out" },
          0.52
        )
        .to(
          backCtaRef.current,
          { y: 0, autoAlpha: 1, duration: 0.35, ease: "power2.out" },
          0.58
        );
    }, root);

    return () => {
      tlRef.current = null;
      ctx.revert();
    };
  }, [reducedMotion]);

  const play = () => {
    const tl = tlRef.current;
    if (!tl) return;
    tl.timeScale(1).play();
  };

  const reverse = () => {
    const tl = tlRef.current;
    if (!tl) return;
    tl.timeScale(1.1).reverse();
  };

  return (
    <article
      ref={rootRef}
      data-service-card
      data-service-id={item.id}
      tabIndex={0}
      onMouseEnter={play}
      onMouseLeave={reverse}
      onFocus={play}
      onBlur={reverse}
      className="service-ribbon-card relative h-[340px] w-[min(360px,78vw)] shrink-0 outline-none md:h-[350px] md:w-[min(390px,30.5vw)] lg:w-[min(400px,28.5vw)]"
      style={{ perspective: "1400px" }}
      data-testid={`service-card-${item.id}`}
      aria-labelledby={`service-title-${item.id}`}
    >
      <div
        ref={innerRef}
        className="service-ribbon-inner relative h-full w-full"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* ——— FRONT ——— */}
        <div
          className="service-ribbon-face service-ribbon-face--front absolute inset-0 flex flex-col rounded-[1.5rem] border border-white/10 bg-[#0a0a0a] px-4 pb-4 pt-4 md:px-5 md:pb-5 md:pt-4"
          data-service-media
        >
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <span
              className="font-accent text-xl italic text-white/35 md:text-2xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {item.index}
            </span>
          </div>

          <div className="service-ribbon-image relative mb-3 min-h-0 flex-1 overflow-hidden rounded-[1.1rem]">
            {item.image ? (
              <div ref={imageRef} className="service-ribbon-image-media absolute inset-0 will-change-transform">
                <Img
                  src={item.image}
                  alt={item.imageAlt || ""}
                  width={800}
                  height={560}
                  sizes="(max-width: 768px) 78vw, 400px"
                  widths={[480, 768, 1024]}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ) : (
              <div className="absolute inset-0 bg-white/[0.04]" aria-hidden />
            )}
            <div className="service-ribbon-image-veil pointer-events-none absolute inset-0" aria-hidden />
          </div>

          <div className="flex items-end justify-between gap-3">
            <h3
              id={`service-title-${item.id}`}
              className="max-w-[15ch] font-display text-[1.45rem] font-semibold leading-[1.08] tracking-tight text-white md:text-[1.7rem]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {item.title}
            </h3>
            <div className="mb-0.5 flex shrink-0 items-center gap-2.5 text-white/45">
              <ServiceIcon name={item.icon} size={18} className="text-white/50" />
              <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
            </div>
          </div>
        </div>

        {/* ——— BACK (unchanged) ——— */}
        <div
          className="service-ribbon-face service-ribbon-face--back service-ribbon-glass absolute inset-0 flex flex-col rounded-[1.5rem] border border-[#93c5fd]/35 px-5 py-5 md:px-6 md:py-6"
          style={{ transform: "rotateY(180deg)" }}
          aria-hidden
        >
          <div className="flex items-start justify-between gap-3">
            <span ref={backIconRef} className="inline-flex text-[#93c5fd]">
              <ServiceIcon name={item.icon} size={22} className="text-[#93c5fd]" />
            </span>
          </div>

          <h3
            ref={backTitleRef}
            className="mt-4 font-display text-[1.45rem] font-semibold leading-[1.1] tracking-tight text-white md:text-[1.7rem]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {item.title}
          </h3>

          <p
            ref={backDescRef}
            data-service-desc
            className="mt-3 flex-1 font-body text-sm leading-relaxed text-[#dbeafe]/90 md:text-[0.95rem]"
          >
            {item.description}
          </p>

          <div ref={backCtaRef} className="mt-4">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                scrollToSection("#contact");
              }}
              className="inline-flex items-center gap-1.5 text-xs tracking-wide text-[#93c5fd]/90 transition-colors hover:text-[#bfdbfe]"
              data-testid={`service-cta-${item.id}`}
            >
              <span>{item.ctaLabel ?? "Discuss This Service"}</span>
              <ArrowRight size={14} strokeWidth={1.6} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  const revealRef = useScrollReveal({ stagger: 0.1, y: 36 });
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const reduced = prefersReducedMotion();

  useHorizontalRibbon(viewportRef, trackRef, { enabled: true });

  return (
    <section
      id="services"
      ref={revealRef}
      className="relative py-7 md:py-10"
      data-testid="services-section"
    >
      <div className="mx-auto mb-4 max-w-[1400px] px-6 md:mb-5 md:px-10">
        <SectionHeading
          reveal
          eyebrow={services.eyebrow}
          title={services.heading}
          titleClassName="max-w-2xl lg:text-5xl"
        />
      </div>

      <div
        ref={viewportRef}
        className="service-ribbon-viewport relative w-full cursor-grab overflow-hidden pl-6 active:cursor-grabbing md:pl-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))]"
        style={{ touchAction: "pan-y" }}
        data-testid="services-ribbon"
        aria-label="Services carousel"
      >
        <div
          ref={trackRef}
          className="service-ribbon-track flex w-max flex-nowrap gap-4 will-change-transform py-2 pr-6 md:gap-5 md:pr-10"
        >
          {services.items.map((item) => (
            <div key={item.id} data-reveal>
              <ServiceCard item={item} reducedMotion={reduced} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
